import { useEffect, useState } from "react";
import type { ReactNode } from "react";

import { accessibilityConfig } from "./accessibility.config";
import {
  AccessibilityContext,
  type AccessibilityState,
  type ContrastMode,
} from "./AccessibilityContext";

const STORAGE_KEY = "lawyer_accessibility";
/* =========================
   Default State
   ========================= */

const defaultState: AccessibilityState = {
  /* Font */
  fontSize: accessibilityConfig.fontSize.default,

  /* Core */
  a11yEnabled: false,

  /* Motion */
  reduceMotion: false,
  userOverrideReduceMotion: false,

  /* Visual helpers */
  highlightLinks: false,
  highlightHeadings: false,
  readableFont: false,

  /* Contrast */
  contrastMode: "dark",
};

/* =========================
   Provider
   ========================= */

export function AccessibilityProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AccessibilityState>(defaultState);
  const [hydrated, setHydrated] = useState(false);

  /* ---------- Load (safe merge) ---------- */
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setState((prev) => ({
          ...prev,
          ...parsed,
        }));
      }
    } catch {}

    setHydrated(true);
  }, []);

  /* ---------- Save ---------- */
  useEffect(() => {
    if (!hydrated) return;

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {}
  }, [state, hydrated]);

  /* ---------- Apply system effects ---------- */
  useEffect(() => {
    if (typeof document === "undefined") return;

    /* Font scale */
    document.documentElement.style.fontSize = `${state.fontSize}%`;

    /* Core accessibility mode */
    document.body.classList.toggle("a11y-mode", state.a11yEnabled);

    /* Visual layers */
    document.body.classList.toggle("a11y-reduce-motion", state.reduceMotion);
    document.body.classList.toggle(
      "a11y-highlight-links",
      state.highlightLinks
    );
    document.body.classList.toggle(
      "a11y-highlight-headings",
      state.highlightHeadings
    );
    document.body.classList.toggle("a11y-readable-font", state.readableFont);

    /* Data attributes (system hooks) */
    document.documentElement.setAttribute(
      "data-a11y",
      state.a11yEnabled ? "true" : "false"
    );

    document.documentElement.setAttribute(
      "data-a11y-reduce-motion",
      state.reduceMotion ? "true" : "false"
    );

    /* Contrast mode */
    document.documentElement.setAttribute("data-contrast", state.contrastMode);
  }, [state]);

  /* ---------- System prefers-reduced-motion sync ---------- */
  useEffect(() => {
    if (typeof window === "undefined") return;

    const media = window.matchMedia("(prefers-reduced-motion: reduce)");

    if (!state.userOverrideReduceMotion) {
      setState((s) => ({
        ...s,
        reduceMotion: media.matches,
      }));
    }

    const handler = () => {
      if (!state.userOverrideReduceMotion) {
        setState((s) => ({
          ...s,
          reduceMotion: media.matches,
        }));
      }
    };

    media.addEventListener("change", handler);
    return () => media.removeEventListener("change", handler);
  }, [state.userOverrideReduceMotion]);

  /* =========================
     API
     ========================= */

  const value = {
    state,

    /* Core */
    enableA11y: () => setState((s) => ({ ...s, a11yEnabled: true })),
    disableA11y: () => setState((s) => ({ ...s, a11yEnabled: false })),
    toggleA11y: () => setState((s) => ({ ...s, a11yEnabled: !s.a11yEnabled })),

    /* Font */
    setFontSize: (size: number) =>
      setState((s) => ({
        ...s,
        fontSize: Math.min(
          accessibilityConfig.fontSize.max,
          Math.max(accessibilityConfig.fontSize.min, size)
        ),
      })),

    /* Motion */
    toggleReduceMotion: () =>
      setState((s) => ({
        ...s,
        reduceMotion: !s.reduceMotion,
        userOverrideReduceMotion: true,
      })),

    setReduceMotion: (value: boolean) =>
      setState((s) => ({
        ...s,
        reduceMotion: value,
      })),

    /* Visual helpers */
    toggleHighlightLinks: () =>
      setState((s) => ({ ...s, highlightLinks: !s.highlightLinks })),

    toggleHighlightHeadings: () =>
      setState((s) => ({ ...s, highlightHeadings: !s.highlightHeadings })),

    toggleReadableFont: () =>
      setState((s) => ({ ...s, readableFont: !s.readableFont })),

    /* Contrast */
    setContrastMode: (mode: ContrastMode) =>
      setState((s) => ({ ...s, contrastMode: mode })),

    /* Reset */
    reset: () => {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {}
      setState(defaultState);
    },
  };

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
    </AccessibilityContext.Provider>
  );
}
